import { useState } from 'react';
import {
  Button,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Input,
  InputGroup,
  Portal,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import { datadogRum } from '@datadog/browser-rum';
import { toast } from 'react-toastify';
import { LuMail } from 'react-icons/lu';
import api from '@services/api';
import ErrorLabel from '@components/ErrorLabel';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';

interface Props {
  handleClose: () => void;
};

const InviteDialog = ({ handleClose }: Props) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    setLoading(true);

    try {
      const response = await api.post('/user/invite', {
        email: data.email,
      });

      if (response?.status === 200) {
        toast.success('Convite enviado com sucesso!');
        handleClose();
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      }
    } catch (error) {
      toast.error('Erro ao enviar convite. Verifique os dados e tente novamente.');
      datadogRum.addError(new Error('Fail to send invite'), {
        error,
        email: data.email,
      });
    } finally {
      setLoading(false);
    };
  };

  return (
    <Dialog.Root size="md" placement="center" open onOpenChange={handleClose} closeOnInteractOutside={false}>
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>Convidar um usuário</Dialog.Title>
            </Dialog.Header>

            <form onSubmit={onSubmit}>
              <Dialog.Body>
                <Field.Root invalid={!!errors?.email}>
                  <Field.Label>E-mail</Field.Label>
                  <InputGroup startElement={<LuMail />} width="full" gap={0}>
                    <Input name="email" type="email" placeholder="exemplo@email.com" variant="subtle" required />
                  </InputGroup>
                  {errors?.email?.map((error, index) => (
                    <Field.ErrorText key={index}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>
                {errors?.general && (
                  <Flex direction="column" my={4}>
                    {errors.general.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Flex>
                )}
              </Dialog.Body>

              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" disabled={loading}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button bg="green.700" color="white" type="submit" loading={loading}>
                  Salvar
                </Button>
              </Dialog.Footer>
            </form>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default InviteDialog;
